import { m } from 'motion/react';
import { Copy, RefreshCw, TriangleAlert } from 'lucide-react';
import { useState } from 'react';

import { ApiError, NetworkError } from '../api/problem';
import { useT } from '../i18n/provider';
import type { Key } from '../i18n/provider';

/**
 * `13 §6`: un error de red es un banner no bloqueante con reintento, un 403 es
 * una frase clara sin jerga, y un 500 enseña un `request_id` que el usuario
 * puede copiar. La distinción importa: ofrecer «reintentar» ante un 422 invita
 * a repetir una petición que va a fallar igual.
 *
 * La prosa del servidor ya viene traducida —el backend resuelve el catálogo
 * contra el locale del usuario—, así que aquí solo se traduce lo que se decide
 * en el cliente: el fallo de red, que nunca llegó a tener respuesta, y el 403,
 * donde se prefiere una frase propia sin código de error porque el usuario no
 * puede actuar sobre ninguno de los dos.
 */
export function ErrorBanner({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const t = useT();
  const [copied, setCopied] = useState(false);

  const retryable = error instanceof NetworkError || (error instanceof ApiError && error.status >= 500);
  const requestId = error instanceof ApiError && error.status >= 500 ? error.problem.request_id : null;

  const copy = () => {
    if (!requestId) {
      return;
    }

    void navigator.clipboard?.writeText(requestId);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <m.div
      role="alert"
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.24, ease: [0.22, 0.8, 0.28, 1] }}
      className="flex flex-wrap items-center gap-3 rounded-md border border-danger-line bg-danger-pale px-4 py-3 text-sm"
    >
      <TriangleAlert className="size-4 shrink-0 text-danger" aria-hidden="true" />
      <span className="grow text-ink">{messageOf(error, t)}</span>

      {requestId ? (
        <button
          type="button"
          onClick={copy}
          title={t('errors.copyReference')}
          className="inline-flex items-center gap-1.5 rounded-xs border border-danger-line/70 px-2 py-1 font-mono text-xs text-ink-soft transition-colors hover:bg-danger-line/25"
        >
          <Copy className="size-3" aria-hidden="true" />
          {copied ? t('common.copied') : requestId}
        </button>
      ) : null}

      {retryable && onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="inline-flex items-center gap-1.5 rounded-xs bg-ink px-3 py-1.5 text-xs font-semibold text-paper transition-transform hover:scale-[1.03]"
        >
          <RefreshCw className="size-3" aria-hidden="true" />
          {t('common.tryAgain')}
        </button>
      ) : null}
    </m.div>
  );
}

function messageOf(error: unknown, t: (key: Key) => string): string {
  if (error instanceof ApiError) {
    if (error.status === 403) {
      return error.problem.detail ?? t('errors.forbidden');
    }

    return error.message;
  }

  if (error instanceof NetworkError) {
    return t('errors.network');
  }

  return t('errors.generic');
}
